// src/Shared/Lib/ResponseHook.ts

import { Dispatch, SetStateAction } from "react";
import { ErrorResponseHelper } from "@/Shared/Helpers";
import { ActionResponse } from "@/Shared/Types";


/**
 * =====================================================
 * Action
 * =====================================================
 *
 * Generic action handler type used by ResponseHook.
 *
 * Supports:
 * - React state setters (useState)
 * - Custom response handlers (Redux, callbacks, etc.)
 *
 * This abstraction allows ResponseHook to be reused
 * across hooks, components, and orchestration layers.
 */
export type Action = Dispatch<SetStateAction<ActionResponse>> | ((response: ActionResponse) => void);


/**
 * =====================================================
 * ResponseHook
 * =====================================================
 *
 * Utility class for standardizing action response handling.
 *
 * Responsibilities:
 * - Normalize loading, success, error, and reset states
 * - Centralize error transformation via ErrorResponseHelper
 * - Decouple response management from component logic
 *
 * This helper is designed to be lightweight, reusable,
 * and framework-agnostic beyond basic React primitives.
 */
export class ResponseHook {
	
	/**
	 * =====================================================
	 * loading
	 * =====================================================
	 *
	 * Sets the response state to "loading".
	 * Typically used at the beginning of async flows.
	 */
	static loading(action: Action) {
		action({ status: "loading" });
	}


	/**
	 * =====================================================
	 * success
	 * =====================================================
	 *
	 * Marks the response as successfully completed.
	 * No payload is required.
	 */
	static success(action: Action) {
		action({ status: "success" });
	}


	/**
	 * =====================================================
	 * error
	 * =====================================================
	 *
	 * Handles error normalization and state update.
	 *
	 * Behavior:
	 * - Attempts to extract a user-displayable error
	 *   using ErrorResponseHelper
	 * - Falls back to a default message if necessary
	 * - Updates the response state accordingly
	 *
	 * Designed to be used as the single exit point
	 * for error handling in async workflows.
	 */
	static error(action: Action, error: any, fallback = "Une erreur est survenue") {
		action(ErrorResponseHelper.getError(error, fallback) as ActionResponse);
		return action;
	}

	/**
	 * =====================================================
	 * reset
	 * =====================================================
	 *
	 * Resets the response state to "idle".
	 * Useful for cleanup or reinitialization.
	 */
	static reset(action: Action ) {
		action({ status: 'idle' });
	}
}